import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { RumInit } from '@maersk-global/telemetry-web-sdk';
import { FilterProvider } from './contexts/FilterContext';
// import {
//   createReactRouterV5Options,
//   ReactIntegration,
// } from '@grafana/faro-react';
import { Assessment, Timeline, Analytics, Dashboard, TrendingUp, List, Cloud, Schedule, ArrowDropDown, ChangeCircle, Warehouse, MapOutlined, GppGood, Speed, Headset } from '@mui/icons-material';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useMsalAuthentication
} from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest } from "./auth/authConfig";
import { callMsGraph } from './auth/graph';
import { setAuthToken } from './services/authFetch';

// Import components
import EnterpriseReliabilityFramework from './components/EnterpriseReliabilityFramework';
import SignalToNoiseRatio from './components/SignalToNoiseRatio';
import SignalIntelligenceOverview from './components/SignalIntelligenceOverview';
import IncidentAnalysis from './components/IncidentAnalysis';
import IncidentsBowler from './components/IncidentsBowler';
import PortfolioHealthTracker from './components/PortfolioHealthTracker';
// import LeadingIndicators from './components/LeadingIndicators'; // Disabled - Operations dashboard
import SysOpsCapacityReview from './components/SysOpsCapacityReview';
import DBOpsCapacityReview from './components/DBOpsCapacityReview';
// import OperationsMonitoring from './components/OperationsMonitoring'; // Disabled - Operations dashboard
// import Resilience from './components/Resilience'; // Commented out — replaced by ResilienceMSAAssessment
import ResilienceMSAAssessment from './components/ResilienceMSAAssessment';
import ChangeAnalyticsAndTrends from './components/ChangeAnalyticsAndTrends';
// import MVCAppView from './components/MVCAppView'; // Removed - MVC Master View deprecated
import NOCDashboard from './components/NOCDashboard';
import WarehouseAvailability from './components/WarehouseAvailability';
import AssetChangeMetrics from './components/AssetChangeMetrics';
import TopsOnboarding from './components/TopsOnboarding';
// import MaerskOneViewDashboard from './components/WarehouseOperations'; // Removed - Deprecated
import IncidentOperationsView from './components/IncidentOperationsView';
import MIMOnCallDashboard from './components/MIMOnCallDashboard';
import P1P2ByRegion from './components/P1P2ByRegion';
import SrePage0Metrics from './components/SrePage0Metrics';
import DecommissioningStatus from './components/DecommissioningStatus';
import APMTIncidentOperations from './components/APMTIncidentOperations';
import AutomationInsights from './components/AutomationInsights';
import SapInsights from './components/SapInsights';

// Constants
const API_KEY = process.env.REACT_APP_RUM_API_KEY;

// Maersk One View Dashboard & TOP Onboarding Status access list
const WAREHOUSE_ALLOWED_EMAILS = new Set([
  'rahul.naidu@maersk.com',
  'touseef.mohammed@maersk.com',
  'durand.rodrigues@maersk.com',
  'nitish.yn@maersk.com',
  'k.harish@maersk.com',
]);

// P1/P2 By Region access list
const P1P2_BY_REGION_ALLOWED_EMAILS = new Set([
  'mahesh.s.bhosale@maersk.com',
]);

// SRE Page 0 Metrics access list
const SRE_PAGE0_ALLOWED_EMAILS = new Set([
  'k.harish@maersk.com',
  'nitish.yn@maersk.com',
]);

// Leading Indicators & Operations Monitoring access list
const OPS_ALLOWED_EMAILS = new Set([
  'm.h.javed@maersk.com',
  'vaishali.parande@maersk.com',
  'k.harish@maersk.com',
  'nilesh.chakkar@maersk.com',
  'shilpitha.swarna@maersk.com',
  'nitish.yn@maersk.com',
  'mahesh.s.bhosale@maersk.com',
]);

// P1/P2 Due To Change and Service Excellence dashboard access list
const CHANGE_AND_SERVICE_EXCELLENCE_ALLOWED_EMAILS = new Set([
  'k.harish@maersk.com',
  'nitish.yn@maersk.com',
]);
// APMT Incident & Operations access list
const APMT_ALLOWED_EMAILS = new Set([
  'nitish.yn@maersk.com',
  'k.harish@maersk.com',
]);

// Signal Intelligence Overview and Signal to Noise Ratio access list
const SIGNAL_DASHBOARD_ALLOWED_EMAILS = new Set([
  'k.harish@maersk.com',
  'nitish.yn@maersk.com',
  'shilpitha.swarna@maersk.com',
  'ram.gupta@maersk.com',
]);



const theme = createTheme({
  palette: {
    primary: {
      main: '#0067b3', // Maersk Blue
    },
    secondary: {
      main: '#001a35', // Maersk Dark Blue
    },
    background: {
      default: '#f5f7fa', // Maersk Grey
      paper: '#ffffff',
    },
    text: {
      primary: '#001a35', // Maersk Dark Blue
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
    h1: {
      fontWeight: 700,
      color: '#001a35',
    },
    h2: {
      fontWeight: 700,
      color: '#001a35',
    },
    h3: {
      fontWeight: 700,
      color: '#001a35',
    },
    h4: {
      fontWeight: 700,
      color: '#001a35',
    },
    h5: {
      fontWeight: 600,
      color: '#001a35',
    },
    h6: {
      fontWeight: 600,
      color: '#001a35',
    },
  },
  shape: {
    borderRadius: 6, // Maersk border radius
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: '#0067b3',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#001a35',
          },
        },
        outlined: {
          borderColor: '#e0e6eb',
          color: '#001a35',
          backgroundColor: '#ffffff',
          '&:hover': {
            borderColor: '#0067b3',
            color: '#0067b3',
            backgroundColor: '#f2f7fa',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '1px solid #e0e6eb',
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: '0.04em',
          color: '#001a35',
          '&.Mui-selected': {
            color: '#0067b3',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#0067b3',
          height: 3,
        },
      },
    },
  },
});

export const initializeRum = (userInfo?: any) => {

  try {
    console.log('Initializing RUM...');

    // Skip RUM initialization if API_KEY is empty
    if (!API_KEY || API_KEY === '') {
      console.warn('RUM initialization skipped: API_KEY is not set');
      return;
    }

    //Check if RumInit is available
    if (typeof RumInit !== 'function') {
      console.warn('RUM initialization skipped: RumInit is not available');
      return;
    }

    const rumConfig: any = {
      app: {
        name: 'ReliabilityFramework-prod',
        version: '1.0',
        environment: 'Prod',
      },
      apiKey: API_KEY,
      sso_user: true,
      instrumentations: {
        console: true,
        xhr: false,
        fetch: true,
        interactions: true
      },
      useSendBeacon: false,
      debug: true,
      traceLog: true
    };

    // Add user information if available
    if (userInfo) {
      rumConfig.user = {
        id: userInfo.id || userInfo.userPrincipalName,
        email: userInfo.mail || userInfo.userPrincipalName,
        name: userInfo.displayName,
        // Add more user attributes as needed
        department: userInfo.department,
        jobTitle: userInfo.jobTitle,
        officeLocation: userInfo.officeLocation
      };
      console.log('RUM initialized with user info:', rumConfig.user);
    }
    const faro = RumInit(rumConfig);
    console.log('RUM Initialized successfully');
    return faro;
  } catch (error) {
    console.error('Failed to initialize RUM:', error);
  }
};

// Navigation component to handle routing
function NavigationContent() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [graphData, setGraphData] = useState(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const srePage0Access = SRE_PAGE0_ALLOWED_EMAILS.has(
    (accounts[0]?.username || '').toLowerCase()
  );
  const restrictedDashboardAccess = CHANGE_AND_SERVICE_EXCELLENCE_ALLOWED_EMAILS.has(
    (accounts[0]?.username || '').toLowerCase()
  );
  const signalDashboardAccess = SIGNAL_DASHBOARD_ALLOWED_EMAILS.has(
    (accounts[0]?.username || '').toLowerCase()
  );

  // Define menu anchor states
  const [reliabilityMenuAnchor, setReliabilityMenuAnchor] = useState<null | HTMLElement>(null);
  const [incidentMenuAnchor, setIncidentMenuAnchor] = useState<null | HTMLElement>(null);
  const [networkMenuAnchor, setNetworkMenuAnchor] = useState<null | HTMLElement>(null);
  const [warehouseMenuAnchor, setWarehouseMenuAnchor] = useState<null | HTMLElement>(null);
  const [serviceDeckMenuAnchor, setServiceDeckMenuAnchor] = useState<null | HTMLElement>(null);

  // Define route type
  interface RouteItem {
    path: string;
    label: string;
    icon: JSX.Element;
    component: JSX.Element;
    disabled: boolean;
    tooltip?: string;
  }

  interface GroupRoute {
    label: string;
    icon: JSX.Element;
    isGroup: true;
    subRoutes: RouteItem[];
    disabled?: boolean;
  }

  interface SingleRoute extends RouteItem {
    isGroup: false;
  }

  type TabRoute = SingleRoute | GroupRoute;

  // Define tab routes with grouping
  const tabRoutes: TabRoute[] = [
    {
      path: '/portfolio-health',
      label: 'Portfolio Health Tracker',
      icon: <Dashboard />,
      component: <PortfolioHealthTracker graphData={graphData} accessToken={accessToken} />,
      disabled: false,
      isGroup: false
    },
    {
      label: 'Reliability & Resilience',
      icon: <Assessment />,
      isGroup: true,
      subRoutes: [
        { path: '/reliability-assessment', label: 'Reliability Assessment', icon: <Assessment />, component: <EnterpriseReliabilityFramework />, disabled: false },
        { path: '/resilience-msa', label: 'Resilience Assessment', icon: <Schedule />, component: <ResilienceMSAAssessment />, disabled: false },
        // { path: '/resilience', label: 'Resilience Assessment (Legacy)', icon: <Schedule />, component: <Resilience />, disabled: false }, // Replaced by Resilience MSA Assessment
        ...(signalDashboardAccess
          ? [
            { path: '/signal-to-noise', label: 'Signal to Noise Ratio', icon: <Timeline />, component: <SignalToNoiseRatio />, disabled: false },
            { path: '/signal-intelligence', label: 'Signal Intelligence Overview', icon: <Speed />, component: <SignalIntelligenceOverview />, disabled: false },
          ]
          : []),
        // { path: '/mvc-app-view', label: 'MVC Master View', icon: <List />, component: <MVCAppView />, disabled: false }, // REMOVED - Deprecated
        { path: '/decommissioning-status', label: 'Decommissioning Status', icon: <ChangeCircle />, component: <DecommissioningStatus />, disabled: false },
      ]
    },
    {
      label: 'Incident & Operations',
      icon: <Analytics />,
      isGroup: true,
      subRoutes: [
        { path: '/incident-analysis', label: 'Major Incident Analysis', icon: <Analytics />, component: <IncidentAnalysis />, disabled: false },
        { path: '/incidents-bowler', label: 'Incidents Bowler', icon: <Analytics />, component: <IncidentsBowler />, disabled: false },
        { path: '/automation-insights', label: 'Automation Insights', icon: <Analytics />, component: <AutomationInsights />, disabled: false },
        { path: '/sap-insights', label: 'SAP Insights', icon: <Analytics />, component: <SapInsights />, disabled: false },
        ...(WAREHOUSE_ALLOWED_EMAILS.has((accounts[0]?.username || '').toLowerCase())
          ? [{ path: '/tops-onboarding', label: 'TOP Onboarding Status', icon: <List />, component: <TopsOnboarding />, disabled: false }]
          : []),
        // ...(OPS_ALLOWED_EMAILS.has((accounts[0]?.username || '').toLowerCase())
        //   ? [{ path: '/leading-indicators', label: 'Leading Indicators', icon: <TrendingUp />, component: <LeadingIndicators />, disabled: false }]
        //   : []), // REMOVED - Leading Indicators disabled
        ...(srePage0Access
          ? [{ path: '/sre-page0-metrics', label: 'SRE Page 0 Metrics', icon: <Assessment />, component: <SrePage0Metrics />, disabled: false }]
          : []),
        // ...(OPS_ALLOWED_EMAILS.has((accounts[0]?.username || '').toLowerCase())
        //   ? [{ path: '/operations-monitoring', label: 'Operations Monitoring (Beta)', icon: <Cloud />, component: <OperationsMonitoring />, disabled: false, tooltip: '🚧 SysOps & DBOps Capacity Planning' }]
        //   : []), // REMOVED - Operations Monitoring disabled
        ...(restrictedDashboardAccess
          ? [{ path: '/change-analytics', label: 'P1/P2 Due To Change', icon: <ChangeCircle />, component: <ChangeAnalyticsAndTrends />, disabled: false }]
          : []),
        { path: '/p1p2-incident-ops-view', label: 'P1/P2 Incident – Ops View', icon: <Assessment />, component: <IncidentOperationsView />, disabled: false },
        { path: '/hedwig-oncall', label: 'Hedwig On-Call Dashboard', icon: <Analytics />, component: <MIMOnCallDashboard />, disabled: false },
        ...(APMT_ALLOWED_EMAILS.has((accounts[0]?.username || '').toLowerCase())
          ? [{ path: '/apmt-incident-ops', label: 'APMT Incident & Operations', icon: <Analytics />, component: <APMTIncidentOperations />, disabled: false }]
          : []),
      ]
    },
    {
      label: 'Network',
      icon: <Cloud />,
      isGroup: true,
      subRoutes: [
        { path: '/noc-dashboard', label: 'NOC Dashboard', icon: <Dashboard />, component: <NOCDashboard graphData={graphData} accessToken={accessToken} />, disabled: false }
      ]
    },
    {
      label: 'Warehouse Operations',
      icon: <Warehouse />,
      isGroup: true,
      subRoutes: [
        // { path: '/maersk-one-view-dashboard', label: 'Maersk One View Dashboard', icon: <Warehouse />, component: <MaerskOneViewDashboard />, disabled: false }, // REMOVED - Deprecated
        ...(P1P2_BY_REGION_ALLOWED_EMAILS.has((accounts[0]?.username || '').toLowerCase())
          ? [{ path: '/p1p2-by-region', label: 'P1/P2 By Region', icon: <MapOutlined />, component: <P1P2ByRegion />, disabled: false }]
          : []),
        { path: '/warehouse-availability', label: 'Warehouse Availability', icon: <Warehouse />, component: <WarehouseAvailability />, disabled: false },
      ]
    },
    ...(restrictedDashboardAccess
      ? [{
        label: 'Service Excellence',
        icon: <GppGood />,
        isGroup: true as const,
        subRoutes: [
          { path: '/service-excellence/asset-change', label: 'Asset & Change Metrics', icon: <Assessment />, component: <AssetChangeMetrics />, disabled: false },
        ]
      }]
      : []),
  ];

  // Flatten all routes for rendering
  const allRoutes: RouteItem[] = tabRoutes.flatMap(route =>
    route.isGroup ? route.subRoutes : [route]
  );

  // Get current tab value based on pathname
  const getCurrentTabValue = () => {
    const currentPath = location.pathname;

    // Find which group or tab the current path belongs to
    for (let i = 0; i < tabRoutes.length; i++) {
      const route = tabRoutes[i];
      if (route.isGroup) {
        // Check if current path is in subRoutes
        if (route.subRoutes && route.subRoutes.some(sub => sub.path === currentPath)) {
          return i;
        }
      }
    }
    return 0;
  };

  const [tabValue, setTabValue] = useState(getCurrentTabValue());

  const fetchUserDetails = useCallback(async () => {
    if (!accounts || accounts.length === 0) {
      console.error('No account found');
      return;
    }

    try {
      console.log('🔄 Fetching fresh tokens...');

      // Acquire token silently
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      setAccessToken(response.accessToken);

      // Use the id_token from login directly as the backend Bearer token.
      // This is the JWT already issued during sign-in — no extra round-trip needed.
      setAuthToken(response.idToken);
      console.log('✅ id_token set as backend Bearer token');
      console.log('⏰ Next refresh in 50 minutes');

      // Call Microsoft Graph API to get user info
      const data = await callMsGraph(response.accessToken);
      setGraphData(data);
      console.log('✅ User details fetched:', data);

    } catch (error) {
      console.error('Error fetching user details:', error);

      // If silent token acquisition fails, try interactive
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        setAccessToken(response.accessToken);
        setAuthToken(response.idToken);

        const data = await callMsGraph(response.accessToken);
        setGraphData(data);

      } catch (interactiveError) {
        console.error('Interactive token acquisition failed:', interactiveError);
      }
    }
  }, [accounts, instance]);

  useEffect(() => {
    if (accounts.length > 0) {
      fetchUserDetails();

      // Set up automatic token refresh every 50 minutes
      const REFRESH_INTERVAL_MINUTES = 50;
      const intervalMs = REFRESH_INTERVAL_MINUTES * 60 * 1000;
      console.log(`⏰ Setting up token auto-refresh (every ${REFRESH_INTERVAL_MINUTES} minutes = ${intervalMs}ms)`);
      console.log(`⏰ Current time: ${new Date().toLocaleTimeString()}`);
      console.log(`⏰ Next refresh at: ${new Date(Date.now() + intervalMs).toLocaleTimeString()}`);

      const refreshInterval = setInterval(() => {
        const now = new Date().toLocaleTimeString();
        console.log(`🔄 Auto-refreshing token at ${now}...`);
        fetchUserDetails();
      }, intervalMs);

      // Cleanup interval on unmount or when accounts change
      return () => {
        console.log('🛑 Clearing token refresh interval');
        clearInterval(refreshInterval);
      };
    }
  }, [accounts, fetchUserDetails]);

  // Update tab value when location changes
  useEffect(() => {
    setTabValue(getCurrentTabValue());
  }, [location.pathname]);

  // Redirect to default route if on root path
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '') {
      navigate('/portfolio-health', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleTabClick = (event: React.MouseEvent, index: number) => {
    const route = tabRoutes[index];

    // If it's a group, always open the menu (even if already selected)
    if (route.isGroup) {
      event.stopPropagation();
      if (index === 1) { // Reliability & Resilience
        setReliabilityMenuAnchor(event.currentTarget as HTMLElement);
      } else if (index === 2) { // Incident & Operations
        setIncidentMenuAnchor(event.currentTarget as HTMLElement);
      } else if (index === 3) { // Network
        setNetworkMenuAnchor(event.currentTarget as HTMLElement);
      } else if (index === 4) { // Maersk One View
        setWarehouseMenuAnchor(event.currentTarget as HTMLElement);
      } else if (index === 5) { // Service Excellence
        setServiceDeckMenuAnchor(event.currentTarget as HTMLElement);
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const route = tabRoutes[newValue];

    // For non-group routes, navigate directly
    if (!route.isGroup) {
      setTabValue(newValue);
      if ('path' in route && route.path) {
        navigate(route.path);
      }
    }
  };

  const handleMenuItemClick = (path: string, groupIndex: number) => {
    navigate(path);
    setTabValue(groupIndex);
    setReliabilityMenuAnchor(null);
    setIncidentMenuAnchor(null);
    setNetworkMenuAnchor(null);
    setWarehouseMenuAnchor(null);
    setServiceDeckMenuAnchor(null);
  };

  const handleMenuClose = () => {
    setReliabilityMenuAnchor(null);
    setIncidentMenuAnchor(null);
    setNetworkMenuAnchor(null);
    setWarehouseMenuAnchor(null);
    setServiceDeckMenuAnchor(null);
  };

  return (
    <div className="bg-white text-maersk-text font-sans min-h-screen">
      {/* Professional Maersk Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e1e5e9',
        }}
      >
        <Toolbar sx={{ minHeight: '60px !important', px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
            <Box
              component="img"
              src="/images/maersk-logo.ico"
              alt="Maersk Logo"
              sx={{
                height: 28,
              }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                fontWeight: 600,
                color: '#2c3e50',
                fontSize: '18px',
              }}
            >
              Operational Excellence
            </Typography>
          </Box>

          {/* Contact Us Icon */}
          <Tooltip title="Contact Us - Technology Operations Platform" arrow>
            <Button
              component="a"
              href="https://teams.microsoft.com/l/message/19:220e0552-dc3b-4bd3-b4d0-bf49f37ee119_2f4e361f-49bb-4a39-8d3c-f102d79f30eb@unq.gbl.spaces/1786355695226?context=%7B%22contextType%22%3A%22chat%22%7D"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                minWidth: 'auto',
                padding: '8px',
                color: '#0067b3',
                '&:hover': {
                  backgroundColor: '#f0f7ff',
                },
              }}
            >
              <Headset sx={{ fontSize: 24 }} />
            </Button>
          </Tooltip>

          {/* User Info and Logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontWeight: 500,
              }}
            >
              Welcome, {accounts[0]?.name || accounts[0]?.username}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => instance.logoutRedirect()}
              sx={{
                borderColor: '#0067b3',
                color: '#0067b3',
                '&:hover': {
                  backgroundColor: '#0067b3',
                  color: 'white',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Professional Navigation */}
      <Box sx={{
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e1e5e9',
        px: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              fontWeight: 500,
              color: '#6c757d',
              textTransform: 'none',
              fontSize: '14px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              position: 'relative',
              '&.Mui-selected': {
                color: '#0067b3',
                fontWeight: 600,
              },
              '&:hover:not(.Mui-disabled)': {
                color: '#0067b3',
              },
              '&.Mui-disabled': {
                color: '#a8a8a8',
                opacity: 0.6,
                cursor: 'not-allowed',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0067b3',
              height: 2,
            },
            '& .MuiSvgIcon-root': {
              fontSize: '18px',
            },
          }}
        >
          {tabRoutes.map((route, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {route.label}
                  {route.isGroup && <ArrowDropDown sx={{ fontSize: '20px' }} />}
                </Box>
              }
              icon={route.icon}
              iconPosition="start"
              disabled={'disabled' in route ? route.disabled : false}
              onClick={(e) => handleTabClick(e, index)}
              sx={{
                minWidth: 'auto',
                px: 2,
              }}
            />
          ))}
        </Tabs>

        {/* Reliability & Resilience Menu */}
        <Menu
          anchorEl={reliabilityMenuAnchor}
          open={Boolean(reliabilityMenuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiPaper-root': {
              mt: 0.5,
              minWidth: 220,
            },
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
              fontSize: '14px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              '&:hover': {
                backgroundColor: '#f2f7fa',
              },
            },
          }}
        >
          {tabRoutes[1] && 'subRoutes' in tabRoutes[1] ? tabRoutes[1].subRoutes.map((subRoute: RouteItem, idx: number) => (
            <MenuItem
              key={idx}
              onClick={() => handleMenuItemClick(subRoute.path, 1)}
              selected={location.pathname === subRoute.path}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                {subRoute.icon}
                {subRoute.label}
              </Box>
            </MenuItem>
          )) : null}
        </Menu>

        {/* Incident & Operations Menu */}
        <Menu
          anchorEl={incidentMenuAnchor}
          open={Boolean(incidentMenuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiPaper-root': {
              mt: 0.5,
              minWidth: 220,
            },
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
              fontSize: '14px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              '&:hover': {
                backgroundColor: '#f2f7fa',
              },
            },
          }}
        >
          {tabRoutes[2] && 'subRoutes' in tabRoutes[2] ? tabRoutes[2].subRoutes.map((subRoute: RouteItem, idx: number) => (
            subRoute.tooltip ? (
              <Tooltip key={idx} title={subRoute.tooltip} placement="left">
                <MenuItem
                  onClick={() => handleMenuItemClick(subRoute.path, 2)}
                  selected={location.pathname === subRoute.path}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    {subRoute.icon}
                    {subRoute.label}
                  </Box>
                </MenuItem>
              </Tooltip>
            ) : (
              <MenuItem
                key={idx}
                onClick={() => handleMenuItemClick(subRoute.path, 2)}
                selected={location.pathname === subRoute.path}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  {subRoute.icon}
                  {subRoute.label}
                </Box>
              </MenuItem>
            )
          )) : null}
        </Menu>

        {/* Network Menu */}
        <Menu
          anchorEl={networkMenuAnchor}
          open={Boolean(networkMenuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiPaper-root': {
              mt: 0.5,
              minWidth: 220,
            },
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
              fontSize: '14px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              '&:hover': {
                backgroundColor: '#f2f7fa',
              },
            },
          }}
        >
          {tabRoutes[3] && 'subRoutes' in tabRoutes[3] ? tabRoutes[3].subRoutes.map((subRoute: RouteItem, idx: number) => (
            <MenuItem
              key={idx}
              onClick={() => handleMenuItemClick(subRoute.path, 3)}
              selected={location.pathname === subRoute.path}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                {subRoute.icon}
                {subRoute.label}
              </Box>
            </MenuItem>
          )) : null}
        </Menu>

        {/* Maersk One View Menu */}
        <Menu
          anchorEl={warehouseMenuAnchor}
          open={Boolean(warehouseMenuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{
            '& .MuiPaper-root': { mt: 0.5, minWidth: 220 },
            '& .MuiMenuItem-root': {
              py: 1.5, px: 2, fontSize: '14px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              '&:hover': { backgroundColor: '#f2f7fa' },
            },
          }}
        >
          {tabRoutes[4] && 'subRoutes' in tabRoutes[4] ? tabRoutes[4].subRoutes.map((subRoute: RouteItem, idx: number) => (
            <MenuItem
              key={idx}
              onClick={() => handleMenuItemClick(subRoute.path, 4)}
              selected={location.pathname === subRoute.path}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                {subRoute.icon}
                {subRoute.label}
              </Box>
            </MenuItem>
          )) : null}
        </Menu>

        {/* Service Excellence Menu */}
        <Menu
          anchorEl={serviceDeckMenuAnchor}
          open={Boolean(serviceDeckMenuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{
            '& .MuiPaper-root': { mt: 0.5, minWidth: 220 },
            '& .MuiMenuItem-root': {
              py: 1.5, px: 2, fontSize: '14px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              '&:hover': { backgroundColor: '#f2f7fa' },
            },
          }}
        >
          {tabRoutes[5] && 'subRoutes' in tabRoutes[5] ? tabRoutes[5].subRoutes.map((subRoute: RouteItem, idx: number) => (
            <MenuItem
              key={idx}
              onClick={() => handleMenuItemClick(subRoute.path, 5)}
              selected={location.pathname === subRoute.path}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                {subRoute.icon}
                {subRoute.label}
              </Box>
            </MenuItem>
          )) : null}
        </Menu>

        {/* Current Selection Display - Same line as tabs */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          ml: 'auto',
          pr: 2,
        }}>
          <Typography
            variant="body2"
            sx={{
              color: '#0067b3',
              fontWeight: 600,
              fontSize: '14px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              whiteSpace: 'nowrap',
            }}
          >
            Current: {(() => {
              const currentPath = location.pathname;
              // Find the current route label
              for (const route of tabRoutes) {
                if (route.isGroup && 'subRoutes' in route) {
                  const subRoute = route.subRoutes.find(sub => sub.path === currentPath);
                  if (subRoute) return subRoute.label;
                } else if ('path' in route && route.path === currentPath) {
                  return route.label;
                }
              }
              return 'Portfolio Health Tracker';
            })()}
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        backgroundColor: '#ffffff',
        minHeight: 'calc(100vh - 109px)', // Adjust for header height
        p: 0,
      }}>
        <Routes>
          {allRoutes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={
                <Box sx={{
                  p: (route.path === '/reliability-assessment' || route.path === '/resilience-msa' || route.path === '/decommissioning-status') ? 0 : route.path === '/p1p2-incident-ops-view' ? 1 : route.path === '/sre-page0-metrics' ? 2 : 4, // No padding for Reliability Assessment and Resilience Assessment (MSA)
                  backgroundColor: '#ffffff',
                  minHeight: '100%',
                }}>
                  {route.component}
                </Box>
              }
            />
          ))}

          {/* Redirect root to portfolio health */}
          <Route path="/" element={<PortfolioHealthTracker graphData={graphData} accessToken={accessToken} />} />
        </Routes>
      </Box>
    </div>
  );
}

function App() {
  // This will automatically redirect to login if user is not authenticated
  // Pass loginRequest to ensure user.Read scope is consented during initial login
  useMsalAuthentication(InteractionType.Redirect, loginRequest);

  const { accounts } = useMsal();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, [accounts]);

  // Initialize RUM on component mount
  useEffect(() => {
    initializeRum();
  }, []);

  // Show loading while checking authentication
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <p>Loading...</p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <FilterProvider>
          {/* Content for authenticated users */}
          <AuthenticatedTemplate>
            <NavigationContent />
          </AuthenticatedTemplate>

          {/* Content for unauthenticated users */}
          <UnauthenticatedTemplate>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh"
              }}
            >
            </div>
          </UnauthenticatedTemplate>
        </FilterProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
